import { Component, inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthModalService } from '../../services/auth-modal.service';
import { ToastService } from '../../services/toast.service';
import { CustomValidators } from '../../custom-validators';
import { UserInterest } from '../../../models/auth.model';
import { ValidationErrorHelper } from '../../../helpers/validation-error.helper';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-register-form',
  imports: [ReactiveFormsModule],
  templateUrl: './register-form.component.html',
  styleUrl: './register-form.component.scss',
})
export class RegisterFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  @Input() returnUrl = '';
  @Input() showLoginLink = true;
  @Output() success = new EventEmitter<void>();
  @Output() switchToLoginRequested = new EventEmitter<void>();

  registerForm: FormGroup;
  isLoading = false;
  serverErrors: string[] = [];
  fieldErrors: { [key: string]: string[] } = {};
  userInterests: UserInterest[] = [];

  constructor() {
    this.registerForm = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(12),
            CustomValidators.requireLowercase(),
            CustomValidators.requireUppercase(),
            CustomValidators.requireDigit(),
            CustomValidators.requireSpecial(),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        jobTitle: [''],
        department: [''],
      },
      { validators: CustomValidators.passwordMatchValidator('password', 'confirmPassword') }
    );
  }

  ngOnInit(): void {
    this.loadUserInterests();
  }

  private loadUserInterests(): void {
    this.authService.getUserInterests().subscribe({
      next: interests => {
        this.userInterests = interests;
        // Add checkbox controls for each interest
        interests.forEach(interest => {
          this.registerForm.addControl(`interest-${interest.id}`, this.fb.control(false));
        });
      },
      error: error => {
        logger.error('Failed to load user interests:', error);
        this.toastService.error('Failed to load interests. You can still register without selecting interests.');
      },
    });
  }

  onSubmit(): void {
    if (this.registerForm.valid && !this.isLoading) {
      this.isLoading = true;
      const formData = this.registerForm.value;

      // Get selected interest IDs
      const selectedInterestIds: number[] = [];
      this.userInterests.forEach(interest => {
        if (formData[`interest-${interest.id}`]) {
          selectedInterestIds.push(interest.id);
        }
      });

      this.authService
        .register({
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          jobTitle: formData.jobTitle || undefined,
          department: formData.department || undefined,
          interestIds: selectedInterestIds.length > 0 ? selectedInterestIds : undefined,
        })
        .subscribe({
          next: response => {
            this.isLoading = false;
            if (response.success) {
              this.success.emit();
              // Navigate to the email confirmation pending page
              // The user must confirm their email before they can log in
              this.router.navigate(['/account/registration-confirmation'], {
                queryParams: { email: formData.email },
              });
            } else {
              this.toastService.error(response.message || 'Registration failed');
            }
          },
          error: error => {
            this.isLoading = false;
            this.clearErrors();

            // Use the new ValidationErrorHelper to process the error
            const validationErrors = ValidationErrorHelper.handleHttpError(error);

            // Apply errors to the form
            ValidationErrorHelper.applyValidationErrorsToForm(this.registerForm, error.error);

            // Set general errors for display
            this.serverErrors = validationErrors.generalErrors;
            this.fieldErrors = validationErrors.fieldErrors;

            logger.error('Registration error:', error);
          },
        });
    }
  }

  private clearErrors(): void {
    this.serverErrors = [];
    this.fieldErrors = {};
    // Clear form validation errors as well
    ValidationErrorHelper.clearFormValidationErrors(this.registerForm);
  }

  switchToLogin(): void {
    if (this.showLoginLink) {
      this.switchToLoginRequested.emit();
    }
  }
}
